import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import Header from '../components/Header';
import Login from '../components/Login';
import { ModelSettingsAPI } from '../services/componentServices';
import useLogout from '../hooks/useLogout';
import LogoutConfirmModal from '../components/LogoutConfirmModal';

const ModelSettingsPage = ({
    currentUser,
    onLogout,
    onShowUserManagement,
    onTabChange,
    activeTab,
    sidebarOpen,
    onToggleSidebar
}) => {
    const [selectedBasis, setSelectedBasis] = useState('volume');
    const [currency, setCurrency] = useState('INR');
    const [volumeUnit, setVolumeUnit] = useState('bbl');
    const [massUnit, setMassUnit] = useState('mt');
    const [isSaved, setIsSaved] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [volumeUnits, setVolumeUnits] = useState([]);
    const [massUnits, setMassUnits] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const {
        showLogoutConfirm,
        handleLogoutClick,
        handleConfirmLogout,
        handleCancelLogout
    } = useLogout();

    const defaultVolumeUnits = [
        { value: 'bbl', label: 'Barrel (bbl)', description: 'Refinery and crude/product blending standard' },
        { value: 'm3', label: 'Cubic meter (m³)', description: 'SI unit, international reporting, custody transfer' },
        { value: 'us_gal', label: 'US Gallon (US gal)', description: 'Intermediate calculations, additive blending' },
        { value: 'L', label: 'Liter (L)', description: 'Lab-scale, small blend or sampling' },
        { value: 'kL', label: 'Kilo Liter (kL)', description: 'Metric system reporting' },
        { value: 'ft3', label: 'Cubic foot (ft³)', description: 'Gasoline vapor or LPG handling' },
        { value: 'mL', label: 'Milliliter (mL)', description: 'Laboratory testing or small-scale blending' },
        { value: 'bcm', label: 'Barrel of cubic meter (bcm)', description: 'Offshore/European systems' }
    ];

    const defaultMassUnits = [
        { value: 'kg', label: 'Kilogram (kg)', description: 'Standard SI unit for refinery mass balance' },
        { value: 'mt', label: 'Metric Ton (MT)', description: 'Product and crude trade reporting' },
        { value: 'lb', label: 'Pound (lb)', description: 'US customary unit, sometimes in additive data sheets' },
        { value: 'ton', label: 'Short Ton (ton)', description: 'US refineries for some heavy oils' },
        { value: 'tonne', label: 'Tonne (tonne)', description: 'Common in international trade' },
        { value: 'g', label: 'Gram (g)', description: 'Laboratory property measurement' },
        { value: 'mg', label: 'Milligram (mg)', description: 'Laboratory or analytical balance' }
    ];

    const defaultCurrencies = [
        { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
        { code: 'USD', symbol: '$', name: 'US Dollar' },
        { code: 'EUR', symbol: '€', name: 'Euro' },
        { code: 'GBP', symbol: '£', name: 'British Pound' },
        { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
        { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
        { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
        { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham (Dubai)' },
        { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal' },
        { code: 'QAR', symbol: '﷼', name: 'Qatari Riyal' },
        { code: 'KWD', symbol: 'د.ك', name: 'Kuwaiti Dinar' },
        { code: 'BHD', symbol: '.د.ب', name: 'Bahraini Dinar' },
        { code: 'OMR', symbol: '﷼', name: 'Omani Rial' }
    ];

    useEffect(() => {
        if (currentUser) {
            loadModelSettings();
            loadAvailableOptions();
        }
    }, [currentUser]);

    const loadModelSettings = async () => {
        try {
            setIsLoading(true);
            const settings = await ModelSettingsAPI.getModelSettings(currentUser.id);

            setSelectedBasis(settings.basis);
            setCurrency(settings.currency);
            setVolumeUnit(settings.volumeUnit);
            setMassUnit(settings.massUnit);

            localStorage.setItem('blendOptimizerModelBasis', settings.basis);
            localStorage.setItem('blendOptimizerCurrency', settings.currency);
            localStorage.setItem('blendOptimizerVolumeUnit', settings.volumeUnit);
            localStorage.setItem('blendOptimizerMassUnit', settings.massUnit);
        } catch (error) {
            console.error('Failed to load model settings from API, using localStorage:', error);
            const savedBasis = localStorage.getItem('blendOptimizerModelBasis');
            const savedCurrency = localStorage.getItem('blendOptimizerCurrency');
            const savedVolumeUnit = localStorage.getItem('blendOptimizerVolumeUnit');
            const savedMassUnit = localStorage.getItem('blendOptimizerMassUnit');

            if (savedBasis) setSelectedBasis(savedBasis);
            if (savedCurrency) setCurrency(savedCurrency);
            if (savedVolumeUnit) setVolumeUnit(savedVolumeUnit);
            if (savedMassUnit) setMassUnit(savedMassUnit);
        } finally {
            setIsLoading(false);
        }
    };

    const loadAvailableOptions = async () => {
        try {
            const options = await ModelSettingsAPI.getAvailableOptions();
            setVolumeUnits(options.volumeUnits);
            setMassUnits(options.massUnits);
            setCurrencies(options.currencies);
        } catch (error) {
            console.error('Failed to load available options from API, using defaults:', error);
            setVolumeUnits(defaultVolumeUnits);
            setMassUnits(defaultMassUnits);
            setCurrencies(defaultCurrencies);
        }
    };

    const handleBasisChange = async (e) => {
        const newBasis = e.target.value;
        setSelectedBasis(newBasis);

        try {
            await ModelSettingsAPI.updateModelSettings(currentUser.id, {
                basis: newBasis,
                currency,
                volumeUnit,
                massUnit
            });
            localStorage.setItem('blendOptimizerModelBasis', newBasis);
            showSaveMessage();
        } catch (error) {
            console.error('Failed to update basis:', error);
            localStorage.setItem('blendOptimizerModelBasis', newBasis);
            showSaveMessage();
        }
    };

    const handleCurrencyChange = async (e) => {
        const newCurrency = e.target.value;
        setCurrency(newCurrency);

        try {
            await ModelSettingsAPI.updateModelSettings(currentUser.id, {
                basis: selectedBasis,
                currency: newCurrency,
                volumeUnit,
                massUnit
            });
            localStorage.setItem('blendOptimizerCurrency', newCurrency);
            showSaveMessage();
        } catch (error) {
            console.error('Failed to update currency:', error);
            localStorage.setItem('blendOptimizerCurrency', newCurrency);
            showSaveMessage();
        }
    };

    const handleVolumeUnitChange = async (e) => {
        const newUnit = e.target.value;
        setVolumeUnit(newUnit);

        try {
            await ModelSettingsAPI.updateModelSettings(currentUser.id, {
                basis: selectedBasis,
                currency,
                volumeUnit: newUnit,
                massUnit
            });
            localStorage.setItem('blendOptimizerVolumeUnit', newUnit);
            showSaveMessage();
        } catch (error) {
            console.error('Failed to update volume unit:', error);
            localStorage.setItem('blendOptimizerVolumeUnit', newUnit);
            showSaveMessage();
        }
    };

    const handleMassUnitChange = async (e) => {
        const newUnit = e.target.value;
        setMassUnit(newUnit);

        try {
            await ModelSettingsAPI.updateModelSettings(currentUser.id, {
                basis: selectedBasis,
                currency,
                volumeUnit,
                massUnit: newUnit
            });
            localStorage.setItem('blendOptimizerMassUnit', newUnit);
            showSaveMessage();
        } catch (error) {
            console.error('Failed to update mass unit:', error);
            localStorage.setItem('blendOptimizerMassUnit', newUnit);
            showSaveMessage();
        }
    };

    const showSaveMessage = () => {
        setIsSaved(true);
        setTimeout(() => setIsSaved(false), 2000);
    };

    const getCurrencySymbol = () => {
        const curr = (currencies.length > 0 ? currencies : defaultCurrencies).find(c => c.code === currency);
        return curr ? curr.symbol : '$';
    };

    const getCurrentUnitDescription = () => {
        if (selectedBasis === 'volume') {
            const unit = (volumeUnits.length > 0 ? volumeUnits : defaultVolumeUnits).find(u => u.value === volumeUnit);
            return unit ? unit.description : '';
        } else {
            const unit = (massUnits.length > 0 ? massUnits : defaultMassUnits).find(u => u.value === massUnit);
            return unit ? unit.description : '';
        }
    };

    const getCurrentUnitLabel = () => {
        if (selectedBasis === 'volume') {
            const unit = (volumeUnits.length > 0 ? volumeUnits : defaultVolumeUnits).find(u => u.value === volumeUnit);
            return unit ? unit.label : 'Volume Unit';
        } else {
            const unit = (massUnits.length > 0 ? massUnits : defaultMassUnits).find(u => u.value === massUnit);
            return unit ? unit.label : 'Mass Unit';
        }
    };

    if (!currentUser) {
        return <Login onLogin={() => { }} users={[]} setUsers={() => { }} />;
    }

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex">
                <Navigation
                    currentUser={currentUser}
                    activeTab={activeTab}
                    onTabChange={onTabChange}
                    onLogout={handleLogoutClick}
                    onShowUserManagement={onShowUserManagement}
                    sidebarOpen={sidebarOpen}
                    onToggleSidebar={onToggleSidebar}
                />
                <div className="flex-1 flex flex-col lg:ml-0">
                    <Header
                        currentUser={currentUser}
                        onToggleSidebar={onToggleSidebar}
                    />
                    <main className="flex-1 p-4 sm:p-6 overflow-auto">
                        <div className="max-w-7xl mx-auto w-full flex items-center justify-center h-64">
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                                <p className="mt-4 text-gray-600">Loading settings...</p>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex">
            <Navigation
                currentUser={currentUser}
                activeTab={activeTab}
                onTabChange={onTabChange}
                onLogout={handleLogoutClick}
                onShowUserManagement={onShowUserManagement}
                sidebarOpen={sidebarOpen}
                onToggleSidebar={onToggleSidebar}
            />

            <div className="flex-1 flex flex-col lg:ml-0">
                <Header
                    currentUser={currentUser}
                    onToggleSidebar={onToggleSidebar}
                />

                <main className="flex-1 p-4 sm:p-6 overflow-auto">
                    <div className="max-w-7xl mx-auto w-full">
                        {isSaved && (
                            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl animate-fadeIn">
                                <div className="flex items-center">
                                    <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 1 0-16-8 8 8 0 0 0 16 0zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <span className="text-green-700 font-medium">Settings saved successfully!</span>
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300 h-fit">
                                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-200">
                                    <div className="flex items-center gap-3 sm:gap-4">
                                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
                                            <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Model Basis & Units</h1>
                                            <p className="text-sm text-gray-600 mt-1">Configure calculation basis and units</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-4 sm:p-6">
                                    <div className="space-y-6">
                                        <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 sm:p-6 border border-gray-100">
                                            <label
                                                htmlFor="model-basis"
                                                className="block text-sm font-medium text-gray-700 mb-3"
                                            >
                                                Select Model Basis
                                            </label>

                                            <div className="relative w-full">
                                                <select
                                                    id="model-basis"
                                                    value={selectedBasis}
                                                    onChange={handleBasisChange}
                                                    className="w-full px-4 pr-10 py-3 border border-gray-300 rounded-xl 
               focus:ring-2 focus:ring-blue-500 focus:border-transparent 
               transition-all duration-200 bg-white text-base font-medium 
               shadow-sm appearance-none"
                                                >
                                                    <option value="volume">Volume Basis</option>
                                                    <option value="mass">Weight (Mass) Basis</option>
                                                </select>

                                                <svg
                                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </div>

                                            <p className="mt-3 text-sm text-gray-500">
                                                This setting determines whether calculations are performed on a volume or mass basis.
                                            </p>
                                        </div>

                                        <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 sm:p-6 border border-gray-100">
                                            <label htmlFor="unit-selection" className="block text-sm font-medium text-gray-700 mb-3">
                                                Select {selectedBasis === 'volume' ? 'Volume' : 'Mass'} Unit
                                            </label>
                                            {selectedBasis === 'volume' ? (
                                                <div className="relative w-full">
                                                    <select
                                                        id="unit-selection"
                                                        value={volumeUnit}
                                                        onChange={handleVolumeUnitChange}
                                                        className="w-full px-4 pr-10 py-3 border border-gray-300 rounded-xl 
               focus:ring-2 focus:ring-blue-500 focus:border-transparent 
               transition-all duration-200 bg-white text-base font-medium 
               shadow-sm appearance-none"
                                                    >
                                                        {(volumeUnits.length > 0 ? volumeUnits : defaultVolumeUnits).map((unit) => (
                                                            <option key={unit.value} value={unit.value}>
                                                                {unit.label}
                                                            </option>
                                                        ))}
                                                    </select>

                                                    <svg
                                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                    </svg>
                                                </div>

                                            ) : (
                                                <div className="relative w-full">
                                                    <select
                                                        id="unit-selection"
                                                        value={massUnit}
                                                        onChange={handleMassUnitChange}
                                                        className="w-full px-4 pr-10 py-3 border border-gray-300 rounded-xl 
               focus:ring-2 focus:ring-blue-500 focus:border-transparent 
               transition-all duration-200 bg-white text-base font-medium 
               shadow-sm appearance-none"
                                                    >
                                                        {(massUnits.length > 0 ? massUnits : defaultMassUnits).map((unit) => (
                                                            <option key={unit.value} value={unit.value}>
                                                                {unit.label}
                                                            </option>
                                                        ))}
                                                    </select>

                                                    <svg
                                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                    </svg>
                                                </div>

                                            )}
                                            <p className="mt-3 text-sm text-gray-500">
                                                {getCurrentUnitDescription()}
                                            </p>
                                        </div>

                                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 sm:p-6 border border-blue-100">
                                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                                <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 1 1 -18 0 9 9 0 0 1 18 0z" />
                                                </svg>
                                                Current Setting
                                            </h3>
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                                <div className="flex items-center mb-3 sm:mb-0">
                                                    <div className={`w-3 h-3 rounded-full mr-3 ${selectedBasis === 'volume' ? 'bg-blue-500' : 'bg-green-500'}`}></div>
                                                    <div>
                                                        <span className="text-lg sm:text-xl font-bold text-gray-900 block">
                                                            {selectedBasis === 'volume' ? 'Volume Basis' : 'Weight Basis'}
                                                        </span>
                                                        <span className="text-sm text-gray-600">
                                                            {getCurrentUnitLabel()}
                                                        </span>
                                                        <span className="text-xs text-gray-500 block mt-1">
                                                            {getCurrentUnitDescription()}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="text-left sm:text-right">
                                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M10 18a8 8 0 1 0-16-8 8 8 0 0 0 16 0zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                        </svg>
                                                        Active
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300 h-fit">
                                <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-200">
                                    <div className="flex items-center gap-3 sm:gap-4">
                                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
                                            <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Currency Settings</h1>
                                            <p className="text-sm text-gray-600 mt-1">Configure default currency</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-4 sm:p-6">
                                    <div className="space-y-6">
                                        <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 sm:p-6 border border-gray-100">
                                            <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-3">
                                                Select Default Currency
                                            </label>
                                            <div className="relative w-full">
                                                <select
                                                    id="currency"
                                                    value={currency}
                                                    onChange={handleCurrencyChange}
                                                    className="w-full px-4 pr-10 py-3 border border-gray-300 rounded-xl 
               focus:ring-2 focus:ring-green-500 focus:border-transparent 
               transition-all duration-200 bg-white text-base font-medium 
               shadow-sm appearance-none"
                                                >
                                                    {(currencies.length > 0 ? currencies : defaultCurrencies).map((curr) => (
                                                        <option key={curr.code} value={curr.code}>
                                                            {curr.symbol} {curr.name} ({curr.code})
                                                        </option>
                                                    ))}
                                                </select>

                                                <svg
                                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </div>

                                            <p className="mt-3 text-sm text-gray-500">
                                                This currency will be used throughout the application for all cost calculations.
                                            </p>
                                        </div>

                                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 sm:p-6 border border-green-100">
                                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                                <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 1 1 -18 0 9 9 0 0 1 18 0z" />
                                                </svg>
                                                Current Setting
                                            </h3>
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                                                <div className="flex items-center mb-3 sm:mb-0">
                                                    <div className="text-2xl font-bold text-gray-900 mr-3">
                                                        {getCurrencySymbol()}
                                                    </div>
                                                    <div>
                                                        <span className="text-lg sm:text-xl font-bold text-gray-900 block">
                                                            {(currencies.length > 0 ? currencies : defaultCurrencies).find(c => c.code === currency)?.name}
                                                        </span>
                                                        <span className="text-sm text-gray-600">
                                                            ({currency})
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="text-left sm:text-right">
                                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M10 18a8 8 0 1 0-16-8 8 8 0 0 0 16 0zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                        </svg>
                                                        Active
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Solver Settings Card */}
                        <div className="mt-6 bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300">
                            <div className="bg-gradient-to-r from-slate-50 to-gray-50 px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-200">
                                <div className="flex items-center gap-3 sm:gap-4">
                                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-r from-slate-500 to-gray-600 flex items-center justify-center flex-shrink-0">
                                        <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Solver Settings</h1>
                                        <p className="text-sm text-gray-600 mt-1">Configure optimization solver parameters</p>
                                    </div>
                                </div>
                            </div>

                            <div className="p-4 sm:p-6">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 sm:p-6 border border-gray-100">
                                        <label htmlFor="solver-gap" className="block text-sm font-medium text-gray-700 mb-2">
                                            Optimality Gap
                                        </label>
                                        <input
                                            id="solver-gap"
                                            type="number"
                                            step="0.001"
                                            min="0"
                                            value={localStorage.getItem('blendOptimizerGap') || '0.01'}
                                            onChange={(e) => {
                                                localStorage.setItem('blendOptimizerGap', e.target.value);
                                                window.dispatchEvent(new Event('solverSettingsChanged'));
                                            }}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white text-base font-medium shadow-sm"
                                        />
                                        <p className="mt-2 text-sm text-gray-500">
                                            The relative gap between the best known solution and the best bound. Lower values give more precise results but may take longer.
                                        </p>
                                    </div>

                                    <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 sm:p-6 border border-gray-100">
                                        <label htmlFor="solver-threads" className="block text-sm font-medium text-gray-700 mb-2">
                                            Solver Threads
                                        </label>
                                        <input
                                            id="solver-threads"
                                            type="number"
                                            min="1"
                                            max="16"
                                            value={localStorage.getItem('blendOptimizerThreads') || '1'}
                                            onChange={(e) => {
                                                localStorage.setItem('blendOptimizerThreads', e.target.value);
                                                window.dispatchEvent(new Event('solverSettingsChanged'));
                                            }}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white text-base font-medium shadow-sm"
                                        />
                                        <p className="mt-2 text-sm text-gray-500">
                                            Number of parallel threads for the solver. More threads can speed up optimization on multi-core systems.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div> 
                    </div>
                </main>
            </div>

            <LogoutConfirmModal 
                isOpen={showLogoutConfirm}
                onConfirm={handleConfirmLogout}
                onCancel={handleCancelLogout}
            />
        </div>
    );
};

export default ModelSettingsPage;